import { DocumentNode } from 'graphql';
import { ExternalLink } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs, NavItem, Section } from '../../../Common/Layout';
import { Locale } from '../../../../__generated__/graphql';
import { RegistrationSummary, SupportaCampaign } from '../../helpers';
import { useBrandColor } from '../../../../common/Common/ThemeProvider';
import { useNavigation } from '../../../Common/useNavigation';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import { useParticipant } from '../../ParticipantProvider';
import CharityPicker, { Charity } from '../../../../common/Fundraising/CharityPicker';
import EditCharityMutation from './EditCharityMutation';
import GetRegistrationSummaryForDashboardQuery from '../GetRegistrationSummaryForDashboardQuery';
import GetSupportaCampaignsQuery from '../GetSupportaCampaignsQuery';
import SupportaSymbol from '../../../../common/Common/SupportaSymbol';
import UI from '../../../../common/UI';
import config from '../../../../config';
import useDocumentTitle from '../../../../common/useDocumentTitle';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

export interface CharityPageProps {
  participantId: string;
  eventId: string;
  token: string;
  refetchQueries?: DocumentNode[];
}

const CharityPage = ({
  participantId, eventId, token,
  refetchQueries = [GetRegistrationSummaryForDashboardQuery, GetSupportaCampaignsQuery],
}: CharityPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { impersonating } = useParticipant();
  const project = useProject();
  const history = useHistory();

  const { data } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
        registrations_limit: 0,
        waitlist_registrations_limit: 0,
        teams_limit: 0,
        include_filtered_teams: false,
      },
      onError: (error) => {
        if (error.graphQLErrors?.length > 0) {
          // Redirect only in case of a normal authentication/validation error.
          history.replace(r('Portal'));
        }
      },
    },
  );

  const { data: supportaData } = useQuery(
    GetSupportaCampaignsQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
      },
    },
  );

  const summary = data?.registrationSummary;

  useDocumentTitle([
    summary?.participant.full_name,
    summary?.event.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(summary?.event.brand_color);

  if (!data || !themeInitialized) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Link to={r('Registrations', {
          eventId,
          projectId: project.id,
          participantId,
          token,
        })}
        >
          {summary.event.title}
        </UI.Link>
        <UI.Strong>{t('charity')}</UI.Strong>
      </Breadcrumbs>

      <CharitySection
        summary={summary}
        companyFundraiser={supportaData?.registrationSummary.event_participant.company_supporta_campaign}
        refetchQueries={refetchQueries}
      />
    </>
  );
};

interface CharitySectionProps {
  summary: RegistrationSummary;
  companyFundraiser?: SupportaCampaign | null;
  refetchQueries?: DocumentNode[];
}

const CharitySection = ({
  summary, companyFundraiser, refetchQueries,
}: CharitySectionProps) => {
  const { t } = useTranslation();
  const { locale, formatCurrency } = useLocale();
  const notifier = useNotifier();

  const supportaLocale = config.supporta.supportedLocales.includes(locale) ? locale : Locale.en;

  const { charities } = summary.event;
  const eventParticipant = summary.event_participant;

  const allowOtherCharity = summary.event.allow_other_charity;

  const [selectedCharity, setSelectedCharity] = useState<Charity | null>(eventParticipant.charity);

  const defaultEnableCompanyFundraisingCampaign = eventParticipant.charity
    ? eventParticipant.enable_company_fundraising_campaign
    : true;

  const [enableCompanyFundraisingCampaign, setEnableCompanyFundraisingCampaign] = useState(
    defaultEnableCompanyFundraisingCampaign,
  );

  const [showCharityPicker, setShowCharityPicker] = useState(() => (
    allowOtherCharity && (
      charities.length === 0 || (!!selectedCharity && !charities.map(({ id }) => id).includes(selectedCharity.id))
    )
  ));

  const toggleCharityPicker = () => {
    setShowCharityPicker((open) => !open);
    setSelectedCharity(null);
  };

  const skip = () => {
    setShowCharityPicker(false);
    setSelectedCharity(null);
  };

  const [editCharity, { error, loading }] = useMutation(
    EditCharityMutation,
    {
      variables: {
        input: {
          event_id: summary.event.id,
          participant_id: summary.participant.id,
          charity_id: selectedCharity?.id,
          enable_company_fundraising_campaign: enableCompanyFundraisingCampaign,
        },
      },
      onCompleted: (data) => {
        setSelectedCharity(data.editCharity.event_participant.charity);
        setEnableCompanyFundraisingCampaign(data.editCharity.event_participant.enable_company_fundraising_campaign);
        notifier.success(t('information_saved'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  return (
    <>
      <Section title={t('fundraising')} spacing="md">
        <UI.Form onSubmit={() => editCharity()}>
          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.Div>
              <UI.Legend sc={{ fontWeight: 500, mb: 1 }}>
                {t('set_up_fundraising')}
              </UI.Legend>
              <UI.Paragraph sc={{ mb: 0 }}>
                {t('link_charity_description')}
                {' '}
                <UI.A
                  href={config.supporta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('about_supporta')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.Paragraph>
            </UI.Div>

            {charities.length > 0 && (
              <UI.FormGrid sc={{ gutter: charities.length > 0 ? 0.25 : 0, mt: 2 }}>
                {charities.map((charity, index) => (
                  <UI.Radio
                    checked={selectedCharity?.id === charity.id}
                    onChange={() => setSelectedCharity(charity)}
                    sc={{
                      box: true,
                      radioTop: selectedCharity?.id === charity.id && charity.logo_url ? 23 : undefined,
                      radioPositionV: selectedCharity?.id !== charity.id && charity.logo_url ? 'center' : undefined,
                    }}
                    id={`Charity_${index}`}
                    key={charity.id}
                  >
                    <UI.GridContainer
                      sc={{
                        columns: charity.logo_url ? '44px 1fr' : '1fr',
                        alignVertical: !charity.description ? 'center' : undefined,
                        gutter: 0.25,
                      }}
                    >
                      {charity.logo_url && (
                        <UI.Div sc={{ padding: 0.75 }}>
                          <img
                            src={charity.logo_url}
                            alt={charity.title}
                            style={{ display: 'block', objectFit: 'contain', width: 35, height: 35 }}
                          />
                        </UI.Div>
                      )}
                      <UI.Div style={{ minWidth: 0 }}>
                        <UI.Span style={{ fontWeight: 500 }}>
                          {charity.title}
                        </UI.Span>
                        {charity.description && (
                          <UI.InputDescription sc={{ color: 'gray.500' }}>
                            {selectedCharity?.id !== charity.id ? (
                              <UI.Truncate>
                                {charity.description}
                              </UI.Truncate>
                            ) : charity.description}
                          </UI.InputDescription>
                        )}
                      </UI.Div>
                    </UI.GridContainer>
                  </UI.Radio>
                ))}
                {allowOtherCharity && (
                  <UI.Radio
                    checked={showCharityPicker}
                    onChange={() => toggleCharityPicker()}
                    sc={{ box: charities.length > 0 }}
                  >
                    {t('common:fundraising_form.choose_another_charity')}
                  </UI.Radio>
                )}
                <UI.Radio
                  checked={!showCharityPicker && !selectedCharity}
                  onChange={() => skip()}
                  sc={{ box: true }}
                >
                  {t('common:fundraising_form.no_thank_you')}
                </UI.Radio>
              </UI.FormGrid>
            )}

            {showCharityPicker && (
              <CharityPicker
                charity={selectedCharity}
                eventId={summary.event.id}
                onChange={(charity) => setSelectedCharity(charity)}
              />
            )}

            {selectedCharity && (
              <UI.InputGroup>
                <UI.InputLabel>
                  {t('team_fundraiser')}
                </UI.InputLabel>
                <UI.Checkbox
                  checked={enableCompanyFundraisingCampaign}
                  onChange={() => setEnableCompanyFundraisingCampaign((enable) => !enable)}
                >
                  {t('enable_company_fundraising_campaign')}
                </UI.Checkbox>
              </UI.InputGroup>
            )}

            <UI.HR sc={{ dashed: true }} />

            <UI.Button
              type="submit"
              sc={{ brand: 'secondary', block: true }}
              disabled={loading || (selectedCharity?.id === eventParticipant.charity?.id
                && enableCompanyFundraisingCampaign === defaultEnableCompanyFundraisingCampaign)}
            >
              {!eventParticipant.charity ? t('enable_fundraising') : t('save')}
            </UI.Button>
          </UI.FormGrid>
        </UI.Form>
      </Section>

      {eventParticipant.charity && (
        <Section title={t('common:supporta')} spacing="md">
          {companyFundraiser && (
            <NavItem
              href={companyFundraiser.url}
              target="_blank"
              rel="noopener noreferrer"
              title={companyFundraiser.title}
              alignVertical="center"
              icon={<ExternalLink />}
              key={companyFundraiser.id}
            >
              <UI.GridContainer sc={{ columns: '1fr 70px', gutter: 0.5, alignVertical: 'center' }}>
                <UI.Div>
                  <UI.H4>
                    {companyFundraiser.title}
                  </UI.H4>
                  <UI.Span>
                    {t('fundraiser_summary', {
                      fundsRaised: formatCurrency(companyFundraiser.funds_raised),
                      charity: companyFundraiser.charity.title,
                    })}
                  </UI.Span>
                </UI.Div>
                <UI.Div>
                  {companyFundraiser.charity.logo_url && (
                    <img
                      src={companyFundraiser.charity.logo_url}
                      style={{ maxWidth: 70, maxHeight: 42, display: 'block', margin: '0 auto' }}
                      alt={companyFundraiser.charity.title}
                    />
                  )}
                </UI.Div>
              </UI.GridContainer>
            </NavItem>
          )}
          <NavItem
            href={`${config.supporta.url}/${supportaLocale}/fundraisers/${eventParticipant.charity.id}/${summary.event.id}`}
            title={t('view_all_fundraisers')}
            target="_blank"
            rel="noopener noreferrer"
            icon={<ExternalLink />}
            alignVertical="center"
          >
            <UI.H4>
              <UI.Icon sc={{ color: 'supporta_emerald' }}>
                <SupportaSymbol />
              </UI.Icon>
              {' '}
              {t('view_all_fundraisers')}
            </UI.H4>
          </NavItem>
        </Section>
      )}
    </>
  );
};

export default CharityPage;
